import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { MewsTeam } from '@mews/enums';
import { AdditionalAuthorizationError, LogData } from '@mews/framework';
import { handleError } from '../middleware';

interface Meta extends Record<string, unknown> {
    disableDefaultErrorHandler?: boolean | ((error: Error) => boolean);
    logError?: {
        team: MewsTeam;
        [key: string]: unknown;
    };
}

declare module '@tanstack/react-query' {
    interface Register {
        queryMeta: Meta;
        mutationMeta: Meta;
    }
}

interface LogErrorParameters {
    logError: ({ error, extra }: LogData) => void;
}

const createMutationCache = ({ logError }: LogErrorParameters) =>
    new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            const { meta } = mutation;

            if (meta?.logError) {
                logError({ error, extra: meta?.logError });
            }

            const { disableDefaultErrorHandler } = meta || {};
            if (
                disableDefaultErrorHandler === true ||
                (typeof disableDefaultErrorHandler === 'function' && disableDefaultErrorHandler(error) === true)
            ) {
                return;
            }
            handleError(error);
        },
    });

const createQueryCache = ({ logError }: LogErrorParameters) =>
    new QueryCache({
        onError: (error, query) => {
            const { meta } = query;

            if (meta?.logError) {
                logError({ error, extra: meta?.logError });
            }

            const { disableDefaultErrorHandler } = meta || {};
            if (
                disableDefaultErrorHandler === true ||
                (typeof disableDefaultErrorHandler === 'function' && disableDefaultErrorHandler(error) === true)
            ) {
                return;
            }
            handleError(error);
        },
    });

export const createReactQueryClient = ({ logError }: LogErrorParameters) =>
    new QueryClient({
        mutationCache: createMutationCache({ logError }),
        queryCache: createQueryCache({ logError }),
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                staleTime: 5 * 60 * 1000,
                retry: (failureCount, error) => {
                    if (error.name === AdditionalAuthorizationError.name) {
                        return false;
                    }

                    return failureCount < 3;
                },
            },
        },
    });
